import {
  Activity,
  DollarSign,
  FileSignature,
  FileText,
  Gauge,
  LifeBuoy,
  Receipt,
  Send,
  SlidersHorizontal,
  StickyNote,
  Workflow,
} from "lucide-react";
import { lazy } from "react";

import type { TabRoute } from "@/lib/routes";

// Lazy-load each tab so visiting a deal only pulls the active tab's
// chunk. Tab components default-export their named component so React
// .lazy resolves cleanly.
const AccountsTab = lazy(() => import("./tabs/AccountsTab"));
const ActivityTab = lazy(() => import("./tabs/ActivityTab"));
const CasesTab = lazy(() => import("./tabs/CasesTab"));
const CommissionTab = lazy(() => import("./tabs/CommissionTab"));
const ContractsTab = lazy(() => import("./tabs/ContractsTab"));
const NotesTab = lazy(() => import("./tabs/NotesTab"));
const PricingInputsTab = lazy(() => import("./tabs/PricingInputsTab"));
const ProposalTab = lazy(() => import("./tabs/ProposalTab"));
const QuotesTab = lazy(() => import("./tabs/QuotesTab"));
const RelationshipsTab = lazy(() => import("./tabs/RelationshipsTab"));
const RfqsTab = lazy(() => import("./tabs/RfqsTab"));

/**
 * RFQ-flow-only tab paths — rendered only when `is_rfq_required` is true;
 * on non-RFQ direct-close deals (MEP/Retail/CCA, or a non-commodity deal
 * that hasn't opted into an RFQ) they don't appear in the bar. RFQ-optional
 * deals (non-commodity product on a non direct-close segment) gain these
 * tabs the moment they create an RFQ — the server flips `is_rfq_required`.
 *
 * `accounts` is here too: associating accounts → collecting LOAs → sending
 * an RFQ is the RFQ flow's opening sequence. Direct-close deals don't go
 * through it (the non-RFQ setup checklist never touches accounts), so the
 * Accounts tab is noise for them.
 *
 * Kept as a `Set` so the filter function below is O(1) per tab.
 */
const RFQ_ONLY_TAB_PATHS = new Set([
  "accounts",
  "rfqs",
  "quotes",
  "pricing-inputs",
  "proposal",
]);

/**
 * All child tab routes in legacy display order. Some are conditionally
 * hidden — see `dealTabRoutesFor(deal)` below. This array is the
 * source of truth; callers should always filter through the helper
 * rather than importing this directly.
 *
 * Legacy order:
 *   Always visible:
 *     Accounts · Commission · Notes and Attachments · Contracts ·
 *     Associations · Activity
 *   RFQ-flow only (gated on `is_rfq_required`):
 *     RFQs · Quotes · Proposal
 *
 * Every tab in legacy is now ported — none of the entries below
 * carry `disabled: true`.
 */
const ALL_DEAL_TAB_ROUTES: ReadonlyArray<TabRoute> = [
  {
    path: "accounts",
    label: "Accounts",
    icon: Gauge,
    element: <AccountsTab />,
    requiredPermission: "deals.accounts.list",
  },
  {
    path: "rfqs",
    label: "RFQs",
    icon: Send,
    element: <RfqsTab />,
    requiredPermission: "deals.rfqs.list",
  },
  {
    path: "quotes",
    label: "Quotes",
    icon: Receipt,
    element: <QuotesTab />,
    requiredPermission: "deals.quotes.list",
  },
  {
    path: "pricing-inputs",
    label: "Pricing Inputs",
    icon: SlidersHorizontal,
    element: <PricingInputsTab />,
    requiredPermission: "deals.pricing_assumptions",
  },
  {
    path: "proposal",
    label: "Proposal",
    icon: FileText,
    element: <ProposalTab />,
    requiredPermission: "deals.proposal.view",
  },
  {
    path: "commission",
    label: "Commission",
    icon: DollarSign,
    element: <CommissionTab />,
    requiredPermission: "deals.commission.view",
  },
  {
    // Between Commission and Contracts, per the ticket (AB#8826). Part of the
    // base deal view — no permission of its own to read; Add New / Edit /
    // Delete check `deals.notes.manage` inside the tab.
    path: "notes",
    label: "Notes and Attachments",
    icon: StickyNote,
    element: <NotesTab />,
  },
  {
    path: "contracts",
    label: "Contracts",
    icon: FileSignature,
    element: <ContractsTab />,
    requiredPermission: "deals.contracts.view",
  },
  {
    path: "relationships",
    label: "Relationships",
    icon: Workflow,
    element: <RelationshipsTab />,
    requiredPermission: "deals.relationships.view",
  },
  {
    // Between Relationships and Activity, where the ticket puts it (AB#8433).
    path: "cases",
    label: "Cases",
    icon: LifeBuoy,
    element: <CasesTab />,
    requiredPermission: "cases.view",
  },
  {
    path: "activity",
    label: "Activity",
    icon: Activity,
    element: <ActivityTab />,
    requiredPermission: "deals.activity.view",
  },
];

/**
 * Returns the tab routes shown in the bar for a given deal, dropping the
 * RFQ-flow-only tabs (RFQs/Quotes/Proposal) when the deal isn't in that
 * flow. Mirrors legacy's conditional rendering.
 *
 * Usage:
 *   · In `DealDetailPage` — pass to `<RouterTabs>`.
 *   · In `lib/routes.tsx` — `ALL_DEAL_TAB_ROUTES` is passed as the
 *     static children list so every path is a valid route at the
 *     router level; hiding them from the bar is a UI concern, not a
 *     routing one. Deep-linking to `/deals/:id/rfqs` on a non-RFQ deal
 *     still resolves (the route exists, just isn't linked to).
 */
export function dealTabRoutesFor(isRfqRequired: boolean): ReadonlyArray<TabRoute> {
  if (isRfqRequired) return ALL_DEAL_TAB_ROUTES;
  return ALL_DEAL_TAB_ROUTES.filter((r) => !RFQ_ONLY_TAB_PATHS.has(r.path));
}

/**
 * Full tab list — used by `lib/routes.tsx` to register every possible
 * child route under `/deals/:id`. The bar display uses
 * `dealTabRoutesFor(deal.is_rfq_required)` to drop RFQ-flow-only
 * entries for non-RFQ deals.
 */
export const DEAL_TAB_ROUTES = ALL_DEAL_TAB_ROUTES;
